import * as THREE from 'three';
import { SimplexNoise } from 'three/examples/jsm/math/SimplexNoise.js';
import { blocks } from './blocks';
import { RNG } from './rng';
import { FLYING_LAND_SIZE } from '@/constants';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from "@/config/firebase"

const geometry = new THREE.BoxGeometry();

export class IslandWorldChunk extends THREE.Group {

  rng = null
  islandId = null;
  constructor(coords, islandId) {
    super();
    this.islandId = islandId;
    this.coords = coords;
    this.data = [];
    this.loaded = false;
    this.size = 64; // Keeping at 64
    this.rng = new RNG(this.getSeedFromIsland(coords, islandId));
    this.disposed = false;
  }

  getSeedFromIsland(coords, islandId) {
    const seedSource = `${islandId || 'island'}:${coords?.x || 0}:${coords?.y || 0}:${coords?.z || 0}`;
    let hash = 2166136261;
    for (let i = 0; i < seedSource.length; i++) {
      hash ^= seedSource.charCodeAt(i);
      hash = Math.imul(hash, 16777619);
    }
    return Math.abs(hash) || 1;
  }

  async generate() {
    if (this.disposed) return;
    this.initializeEmpty();
    this.proceduralIslandShape();
    this.generateFlatClouds();
    this.generateWater();

    // this.generateMeshes();
    this.loaded = true;
  }

  initializeEmpty() {
    // Initialize 3D data array for terrain generation
    this.data = [];
    for (let x = 0; x < this.size; x++) {
      const slice = [];
      for (let y = 0; y < this.size; y++) {
        const row = [];
        for (let z = 0; z < this.size; z++) {
          row.push({ id: blocks.empty.id, instanceId: null });
        }
        slice.push(row);
      }
      this.data.push(slice);
    }

    // Also keep idMap and instanceIdMap for performance
    this.idMap = new Uint8Array(this.size * this.size * this.size);
    this.instanceIdMap = new Int32Array(this.size * this.size * this.size).fill(-1);
  }

  _getIndex(x, y, z) {
    return x * this.size * this.size + y * this.size + z;
  }


  proceduralIslandShape() {
    const simplex = new SimplexNoise(this.rng);

    const centerX = 32;
    const centerZ = 32;
    const centerY = 32;

    // Keep the radius at 48 for good width, but constrain within bounds
    const radius = Math.min(48, this.size / 2 - 2);
    // Adjusted baseThickness to prevent holes
    const baseThickness = 8;
    const surfaceVariation = 3;  // Reduced for flatter surface
    const cornerSmoothness = 0.90;  // Increased for smoother edges
    const ellipsoidFactor = 0.65;  // Adjusted for better shape

    // Enhanced terrain features
    const addHills = FLYING_LAND_SIZE;
    const hillFrequency = 0.07;  // Lower frequency for gentler hills
    const hillHeight = 4;  // Reduced for less dramatic hills

    // Add biome variation
    const biomeScale = 0.015;
    const temperatureThreshold = 0.2;  // For snow biome
    const humidityThreshold = 0.6;     // For jungle biome
    const desertThreshold = -0.3;      // For desert biome

    // Create a solid base for the island to prevent holes
    for (let x = Math.max(0, centerX - radius); x <= Math.min(this.size - 1, centerX + radius); x++) {
      for (let z = Math.max(0, centerZ - radius); z <= Math.min(this.size - 1, centerZ + radius); z++) {
        const dx = (x - centerX) / radius;
        const dz = (z - centerZ) / radius;
        const distSquared = dx * dx + dz * dz;
        if (distSquared > 1) continue;

        const normalizedDist = Math.sqrt(distSquared);
        if (normalizedDist > cornerSmoothness && this.rng.random() > (1 - normalizedDist)) continue;

        // Biome determination
        const temperature = simplex.noise(x * biomeScale, z * biomeScale);
        const humidity = simplex.noise((x + 500) * biomeScale, (z + 500) * biomeScale);

        // Calculate base height with more variation
        let heightVariation = Math.floor(surfaceVariation * simplex.noise(
          x / (radius / 2),
          z / (radius / 2)
        ));

        // Add hills if enabled
        if (addHills) {
          const hillNoise = simplex.noise(x * hillFrequency, z * hillFrequency);
          heightVariation += Math.floor(hillHeight * Math.max(0, hillNoise));
        }

        const surfaceY = centerY + heightVariation;

        // Calculate thickness with more consistency at edges
        const thickness = Math.max(6, Math.floor(baseThickness * (1 - ellipsoidFactor * normalizedDist)));

        // Determine surface block type based on biome
        let surfaceBlockId = blocks.grass.id;

        if (temperature > temperatureThreshold) {
          surfaceBlockId = blocks.snow.id;
        } else if (temperature < desertThreshold) {
          surfaceBlockId = blocks.sand.id;
        } else if (humidity > humidityThreshold) {
          surfaceBlockId = blocks.jungleGrass.id;
        }

        if (this.inBounds(x, surfaceY, z)) {
          this.setBlockId(x, surfaceY, z, surfaceBlockId);

          // Add a single dirt layer below the surface for stability
          if (this.inBounds(x, surfaceY - 1, z)) {
            this.setBlockId(x, surfaceY - 1, z, blocks.dirt.id);
          }
        }

        // Add stone and resources below (more solid base)
        for (let y = 2; y <= thickness; y++) {
          const yy = surfaceY - y;
          if (this.inBounds(x, yy, z)) {
            // Base layer is stone
            let blockId = blocks.stone.id;

            // Add ore veins
            if (y > 5) {
              // Ore generation chance increases with depth
              const oreChance = Math.min(0.25, y / (thickness * 2));
              if (this.rng.random() < oreChance) {
                // Choose ore type based on depth
                if (y > thickness * 0.7 && this.rng.random() < 0.3) {
                  blockId = blocks.ironOre.id;
                } else if (y > thickness * 0.4 && this.rng.random() < 0.4) {
                  blockId = blocks.coalOre.id;
                }
              }
            }

            // Add lava core at the very bottom
            if (y > thickness - 2) {
              blockId = blocks.lava.id;
            }

            this.setBlockId(x, yy, z, blockId);
          }
        }
      }
    }

    // Add decorations
    this.addDecorations(simplex);
  }

  addDecorations(simplex) {
    // Add trees based on biome
    const treeCount = 10;

    for (let i = 0; i < treeCount; i++) {
      const x = Math.floor(this.rng.random() * (this.size - 14)) + 7;
      const z = Math.floor(this.rng.random() * (this.size - 14)) + 7;

      // Find the surface height at this position
      for (let y = this.size - 1; y >= 0; y--) {
        const blockId = this.getBlock(x, y, z).id;

        if (blockId === blocks.grass.id) {
          // Regular tree
          this.generateTree(x, y + 1, z, false);
          break;
        } else if (blockId === blocks.jungleGrass.id) {
          // Jungle tree
          this.generateTree(x, y + 1, z, true);
          break;
        } else if (blockId === blocks.sand.id) {
          // Cactus in desert
          this.generateCactus(x, y + 1, z);
          break;
        }
      }
    }

    // Add flatter clouds
    // this.generateFlatClouds();
  }

  generateFlatClouds() {
    const cloudCount = 7;
    const cloudLevel = this.size / 2 + 18; // Cloud height

    for (let i = 0; i < cloudCount; i++) {
      const cloudX = Math.floor(this.rng.random() * (this.size - 20)) + 10;
      const cloudZ = Math.floor(this.rng.random() * (this.size - 20)) + 10;
      const cloudWidth = Math.floor(this.rng.random() * 6) + 8; // Width of cloud
      const cloudLength = Math.floor(this.rng.random() * 6) + 8; // Length of cloud

      // Generate a flat cloud patch
      for (let cx = cloudX; cx < cloudX + cloudWidth; cx++) {
        for (let cz = cloudZ; cz < cloudZ + cloudLength; cz++) {
          // Create fluffy cloud edges
          const dx = (cx - cloudX) / cloudWidth - 0.5;
          const dz = (cz - cloudZ) / cloudLength - 0.5;
          const distFromCenter = Math.sqrt(dx * dx + dz * dz) * 2; // Normalized distance

          // Only place cloud blocks within an oval shape for natural look
          if (distFromCenter <= 0.8 + (this.rng.random() * 0.2 - 0.1)) {
            // Vary cloud height slightly for a bit of texture
            const cloudY = cloudLevel + (this.rng.random() < 0.3 ? 1 : 0);

            if (this.inBounds(cx, cloudY, cz)) {
              if (this.getBlock(cx, cloudY, cz).id === blocks.empty.id) {
                this.setBlockId(cx, cloudY, cz, blocks.cloud.id);
              }
            }
          }
        }
      }
    }
  }

  generateTree(x, y, z, isJungle) {
    const height = isJungle
      ? Math.floor(this.rng.random() * 3) + 5  // Jungle trees
      : Math.floor(this.rng.random() * 2) + 3; // Regular trees

    // Tree trunk
    const trunkBlock = isJungle ? blocks.jungleTree : blocks.treeTop;
    if (!trunkBlock) return;
    const trunkId = trunkBlock.id;
    for (let i = 0; i < height; i++) {
      if (this.inBounds(x, y + i, z)) {
        this.setBlockId(x, y + i, z, trunkId);
      }
    }

    // Tree leaves
    const leafBlock = isJungle ? blocks.jungleLeaves : blocks.leave;
    if (!leafBlock) return;
    const leafId = leafBlock.id;
    const leafRadius = isJungle ? 2 : 2;

    for (let lx = x - leafRadius; lx <= x + leafRadius; lx++) {
      for (let ly = y + height - 2; ly <= y + height; ly++) {
        for (let lz = z - leafRadius; lz <= z + leafRadius; lz++) {
          const dx = lx - x;
          const dy = ly - (y + height);
          const dz = lz - z;
          const distSquared = dx * dx + dy * dy + dz * dz;

          if (distSquared <= leafRadius * leafRadius && this.inBounds(lx, ly, lz)) {
            if (this.getBlock(lx, ly, lz).id === blocks.empty.id) {
              this.setBlockId(lx, ly, lz, leafId);
            }
          }
        }
      }
    }
  }

  generateCactus(x, y, z) {
    const height = Math.floor(this.rng.random() * 2) + 2;

    for (let i = 0; i < height; i++) {
      if (this.inBounds(x, y + i, z)) {
        this.setBlockId(x, y + i, z, blocks.cactus.id);
      }
    }
  }

  generateWater() {
    const material = new THREE.MeshLambertMaterial({
      color: 0x9090e0,
      transparent: true,
      opacity: 0.5,
      side: THREE.DoubleSide
    });
    const waterMesh = new THREE.Mesh(new THREE.PlaneGeometry(), material);
    waterMesh.rotateX(-Math.PI / 2);
    waterMesh.position.set(this.size / 2, 20, this.size / 2); // Adjusted water level
    waterMesh.scale.set(this.size * 1.5, this.size * 1.5, 1); // Make water extend beyond island
    waterMesh.layers.set(1);
    this.add(waterMesh);
  }

  generateMeshes() {
    if (this.disposed) return;
    this.clear(); // Clear old children

    // 1st Pass: Count instances per block type
    const counts = {};
    for (let x = 0; x < this.size; x++) {
      for (let y = 0; y < this.size; y++) {
        for (let z = 0; z < this.size; z++) {
          const block = this.getBlock(x, y, z);
          if (block.id === blocks.empty.id || this.isBlockObscured(x, y, z)) continue;
          counts[block.id] = (counts[block.id] || 0) + 1;
        }
      }
    }

    // 2nd Pass: Create InstancedMeshes with exact capacity
    const meshes = {};
    const matrix = new THREE.Matrix4();

    Object.keys(counts).forEach(blockId => {
      const numericBlockId = Number(blockId);
      const blockType = Object.values(blocks).find(b => b.id === numericBlockId);
      if (blockType) {
        const mesh = new THREE.InstancedMesh(geometry, blockType.material, counts[blockId]);
        mesh.count = 0; // We'll increment as we fill
        mesh.name = String(numericBlockId);
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        mesh.frustumCulled = false; // Fix for disappearing chunks
        meshes[numericBlockId] = mesh;
      }
    });

    // 3rd Pass: Fill matrices
    for (let x = 0; x < this.size; x++) {
      for (let y = 0; y < this.size; y++) {
        for (let z = 0; z < this.size; z++) {
          const block = this.getBlock(x, y, z);
          if (block.id === blocks.empty.id || this.isBlockObscured(x, y, z)) continue;

          const mesh = meshes[block.id];
          if (!mesh) continue;

          matrix.setPosition(x, y, z);
          mesh.setMatrixAt(mesh.count, matrix);

          const index = this._getIndex(x, y, z);
          this.instanceIdMap[index] = mesh.count;
          mesh.count++;
        }
      }
    }

    this.add(...Object.values(meshes));
    this.generateWater(); // Add water plane
  }

  isBlockObscured(x, y, z) {
    const neighbors = [
      this.getBlock(x, y + 1, z),
      this.getBlock(x, y - 1, z),
      this.getBlock(x + 1, y, z),
      this.getBlock(x - 1, y, z),
      this.getBlock(x, y, z + 1),
      this.getBlock(x, y, z - 1)
    ];
    return neighbors.every(b => b?.id !== undefined && b.id !== blocks.empty.id);
  }

  getBlock(x, y, z) {
    return this.inBounds(x, y, z) ? this.data[x][y][z] : { id: blocks.empty.id, instanceId: null };
  }

  setBlockId(x, y, z, id) {
    if (this.inBounds(x, y, z)) {
      // Update 3D data array
      this.data[x][y][z].id = id;
      if (id === blocks.empty.id) {
        this.data[x][y][z].instanceId = null;
      }

      // Also update idMap for consistency
      const index = this._getIndex(x, y, z);
      this.idMap[index] = id;
    }
  }

  addBlock(x, y, z, blockId) {
    const localX = Math.floor(x - this.position.x);
    const localY = Math.floor(y - this.position.y);
    const localZ = Math.floor(z - this.position.z);

    if (this.inBounds(localX, localY, localZ)) {
      this.setBlockId(localX, localY, localZ, blockId);
      this.addBlockInstance(localX, localY, localZ);
    }
  }

  removeBlock(x, y, z) {
    const localX = Math.floor(x - this.position.x);
    const localY = Math.floor(y - this.position.y);
    const localZ = Math.floor(z - this.position.z);

    if (this.inBounds(localX, localY, localZ)) {
      const block = this.getBlock(localX, localY, localZ);
      if (block.id !== blocks.empty.id) {
        this.deleteBlockInstance(localX, localY, localZ);
        this.setBlockId(localX, localY, localZ, blocks.empty.id);
      }
    }
  }

  addBlockInstance(x, y, z) {
    const block = this.getBlock(x, y, z);
    const index = this._getIndex(x, y, z);
    if (block.id === blocks.empty.id || this.instanceIdMap[index] !== -1) return;

    let mesh = this.getObjectByName(String(block.id));

    // Lazy creation if mesh doesn't exist
    if (!mesh) {
      const blockType = Object.values(blocks).find(b => b.id === block.id);
      if (!blockType) return;
      mesh = new THREE.InstancedMesh(geometry, blockType.material, 100); // Initial small capacity
      mesh.count = 0;
      mesh.name = String(block.id);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.frustumCulled = false; // Fix for disappearing chunks
      this.add(mesh);
    }

    // Dynamic Resizing if full
    if (mesh.count >= mesh.instanceMatrix.count) {
      const newCapacity = Math.ceil(mesh.instanceMatrix.count * 1.5);
      const newMesh = new THREE.InstancedMesh(mesh.geometry, mesh.material, newCapacity);

      const matrix = new THREE.Matrix4();
      for (let i = 0; i < mesh.count; i++) {
        mesh.getMatrixAt(i, matrix);
        newMesh.setMatrixAt(i, matrix);
      }

      newMesh.count = mesh.count;
      newMesh.name = mesh.name;
      newMesh.castShadow = mesh.castShadow;
      newMesh.receiveShadow = mesh.receiveShadow;
      newMesh.frustumCulled = false; // Fix for disappearing chunks

      this.remove(mesh);
      mesh.dispose();
      mesh = newMesh;
      this.add(mesh);
    }

    const matrix = new THREE.Matrix4();
    matrix.setPosition(x, y, z);

    this.instanceIdMap[index] = mesh.count;
    this.data[x][y][z].instanceId = mesh.count;
    mesh.setMatrixAt(mesh.count, matrix);
    mesh.count++;
    mesh.instanceMatrix.needsUpdate = true;
  }

  deleteBlockInstance(x, y, z) {
    const block = this.getBlock(x, y, z);
    const currentBlockIndex = this._getIndex(x, y, z);
    const currentInstanceId = this.instanceIdMap[currentBlockIndex];
    if (block.id === blocks.empty.id || currentInstanceId === -1) return;

    const mesh = this.getObjectByName(String(block.id));
    if (!mesh || mesh.count === 0) return;

    // To remove, swap with last instance
    const lastIdx = mesh.count - 1;
    if (currentInstanceId < lastIdx) {
      const lastInstanceMatrix = new THREE.Matrix4();
      mesh.getMatrixAt(lastIdx, lastInstanceMatrix);
      mesh.setMatrixAt(currentInstanceId, lastInstanceMatrix);

      // We need to update the entry in instanceIdMap that pointed to lastIdx
      // Find where [x,y,z] that was at lastIdx is.
      // Instead of triple loop, we only scan the bounding box or just use the position from matrix
      const pos = new THREE.Vector3();
      pos.setFromMatrixPosition(lastInstanceMatrix);

      const lx = Math.round(pos.x);
      const ly = Math.round(pos.y);
      const lz = Math.round(pos.z);

      if (this.inBounds(lx, ly, lz)) {
        const lIndex = this._getIndex(lx, ly, lz);
        this.instanceIdMap[lIndex] = currentInstanceId;
        this.data[lx][ly][lz].instanceId = currentInstanceId;
      }
    }

    // Clear the current block's instance reference
    this.instanceIdMap[currentBlockIndex] = -1;
    this.data[x][y][z].instanceId = null;

    // Decrement count
    mesh.count--;
    mesh.instanceMatrix.needsUpdate = true;
  }

  dispose() {
    this.disposed = true;
    this.traverse(obj => {
      if (obj instanceof THREE.InstancedMesh) {
        obj.dispose();
      } else if (obj.geometry) {
        obj.geometry.dispose();
      }
      if (obj.material) {
        if (Array.isArray(obj.material)) {
          obj.material.forEach(m => m.dispose());
        } else {
          obj.material.dispose();
        }
      }
    });
    this.clear();
  }

  inBounds(x, y, z) {
    return (
      x >= 0 && x < this.size &&
      y >= 0 && y < this.size &&
      z >= 0 && z < this.size
    );
  }
}
